/**
 * Analytics Types and Interfaces
 * Defines all event types and data structures for analytics tracking
 */

import { Timestamp } from 'firebase/firestore';

// Device information
export interface DeviceInfo {
  type: 'mobile' | 'tablet' | 'desktop' | 'unknown';
  browser: string;
  os: string;
  isVR: boolean;
  screenWidth?: number;
  screenHeight?: number;
}

// Performance metrics
export interface PerformanceMetrics {
  fps?: number;
  loadTime?: number;
  memoryUsage?: number;
}

// Event types enum
export enum AnalyticsEventType {
  // Session events
  SESSION_STARTED = 'session_started',
  SESSION_ENDED = 'session_ended',

  // Sequence events
  SEQUENCE_LOADED = 'sequence_loaded',
  SEQUENCE_VIEW_STARTED = 'sequence_view_started',
  SEQUENCE_VIEW_COMPLETED = 'sequence_view_completed',
  SEQUENCE_PASSWORD_REQUIRED = 'sequence_password_required',
  SEQUENCE_PASSWORD_SUCCESS = 'sequence_password_success',
  SEQUENCE_PASSWORD_FAILURE = 'sequence_password_failure',

  // Stage navigation events
  STAGE_NAVIGATION = 'stage_navigation',
  STAGE_VIEW_STARTED = 'stage_view_started',
  STAGE_VIEW_ENDED = 'stage_view_ended',
  STAGE_SELECTOR_OPENED = 'stage_selector_opened',
  STAGE_SELECTOR_CLOSED = 'stage_selector_closed',

  // VR events
  VR_SESSION_STARTED = 'vr_session_started',
  VR_SESSION_ENDED = 'vr_session_ended',
  VR_STATE_CHANGED = 'vr_state_changed',
  VR_CONTROLLER_CONNECTED = 'vr_controller_connected',

  // Media events
  VIDEO_PLAY = 'video_play',
  VIDEO_PAUSE = 'video_pause',
  VIDEO_COMPLETE = 'video_complete',
  VIDEO_ERROR = 'video_error',
  VIDEO_SEEK = 'video_seek',

  // User interaction events
  FOV_ZOOM_USED = 'fov_zoom_used',
  CONTROLLER_BUTTON_PRESSED = 'controller_button_pressed',

  // Performance events
  PERFORMANCE_METRIC = 'performance_metric',
  STAGE_LOAD_TIME = 'stage_load_time',
  MEDIA_LOAD_TIME = 'media_load_time',

  // Error events
  ERROR_OCCURRED = 'error_occurred',

  // Lead capture form events
  LEAD_FORM_VIEWED = 'lead_form_viewed',
  LEAD_FORM_SUBMITTED = 'lead_form_submitted',
  LEAD_FORM_ABANDONED = 'lead_form_abandoned',
  LEAD_FORM_ERROR = 'lead_form_error',

  // Future: Head tracking (infrastructure only, not active yet)
  HEAD_TRACKING = 'head_tracking',
}

// Base event data interface
export interface BaseEventData {
  timestamp: number;
  sessionId: string;
  sequenceId: string | null;
  userId: string | null;
}

// Specific event data interfaces

export interface SessionEventData extends BaseEventData {
  duration?: number; // For session_ended
}

export interface SequenceEventData extends BaseEventData {
  sequenceName?: string;
  isPublic?: boolean;
  hasPassword?: boolean;
  organizationId?: string | null;
}

export interface StageNavigationData extends BaseEventData {
  fromStage: number;
  toStage: number;
  method: 'next' | 'previous' | 'jump' | 'selector';
  totalStages: number;
}

export interface StageViewData extends BaseEventData {
  stageIndex: number;
  stageName?: string;
  duration?: number; // For stage_view_ended
  hasVideo?: boolean;
  hasModels?: boolean;
  hasPlanes?: boolean;
  hasAudio?: boolean;
}

export interface StageSelectorData extends BaseEventData {
  mode: '2D' | 'VR';
  stageIndex: number;
}

export interface VRSessionData extends BaseEventData {
  duration?: number; // For vr_session_ended
  state?: 'entering' | 'in' | 'exiting' | 'not_in'; // For vr_state_changed
}

export interface VRControllerData extends BaseEventData {
  handedness: 'left' | 'right' | 'unknown';
  controllerType?: string;
}

export interface VideoEventData extends BaseEventData {
  stageIndex: number;
  videoUrl?: string;
  currentTime?: number;
  duration?: number;
  errorMessage?: string; // For video_error
}

export interface FOVZoomData extends BaseEventData {
  newFOV: number;
  previousFOV: number;
  method: 'wheel' | 'pinch';
}

export interface ControllerButtonData extends BaseEventData {
  button: 'trigger-left' | 'trigger-right' | 'a' | 'b' | 'x' | 'y';
  action: string;
  stageIndex?: number;
}

export interface PerformanceEventData extends BaseEventData {
  stageIndex?: number;
  fps?: number;
  loadTime?: number;
  resourceType?: 'stage' | 'media' | 'model' | 'video' | 'image';
  resourceUrl?: string;
}

export interface ErrorEventData extends BaseEventData {
  errorType: string;
  errorMessage: string;
  stack?: string;
  stageIndex?: number;
}

export interface HeadTrackingData extends BaseEventData {
  stageIndex: number;
  // Aggregated gaze data (not individual tracking for privacy)
  gazePoints?: Array<{
    x: number;
    y: number;
    z: number;
    duration: number;
  }>;
}

export interface LeadFormEventData extends BaseEventData {
  stageIndex: number;
  hotspotId: string;
  formType: string;
  isVR: boolean;
  errorMessage?: string; // For lead_form_error
  fieldCount?: number;   // Number of fields in the form
  filledFields?: number; // Number of fields filled before abandonment
}

// Union type for all event data
export type AnalyticsEventData =
  | SessionEventData
  | SequenceEventData
  | StageNavigationData
  | StageViewData
  | StageSelectorData
  | VRSessionData
  | VRControllerData
  | VideoEventData
  | FOVZoomData
  | ControllerButtonData
  | PerformanceEventData
  | ErrorEventData
  | HeadTrackingData
  | LeadFormEventData;

// Firestore document structure for analytics_events collection
export interface AnalyticsEvent {
  sessionId: string;
  sequenceId: string | null;
  userId: string | null;
  timestamp: Timestamp;
  eventType: AnalyticsEventType;
  eventData: AnalyticsEventData;
  deviceInfo: DeviceInfo;
  performanceMetrics?: PerformanceMetrics;
}

// Batch queue item (in-memory before writing to Firestore)
export interface QueuedEvent {
  eventType: AnalyticsEventType;
  eventData: AnalyticsEventData;
  timestamp: number;
}

// Analytics configuration
export interface AnalyticsConfig {
  enabled: boolean;
  batchInterval: number; // milliseconds
  maxBatchSize: number;
  debugMode: boolean;
}

// Dashboard aggregation interfaces
export interface SequenceAnalyticsSummary {
  sequenceId: string;
  sequenceName: string;
  totalViews: number;
  uniqueViewers: number;
  averageSessionDuration: number;
  completionRate: number; // % who viewed all stages
  vrAdoptionRate: number; // % who entered VR
  mostViewedStage: number;
  leastViewedStage: number;
  averageFPS: number;
  errorCount: number;
  deviceBreakdown: {
    mobile: number;
    tablet: number;
    desktop: number;
  };
  lastViewed: Timestamp;
}

export interface LiveViewerData {
  sessionId: string;
  sequenceId: string;
  currentStage: number;
  isInVR: boolean;
  deviceType: string;
  startTime: Timestamp;
}

// Aggregated analytics types for dashboard
export interface SessionAnalytics {
  id: string;
  sessionId: string;
  sequenceId: string | null;
  userId: string | null;
  timestamp: Timestamp;
  deviceInfo: DeviceInfo;
  isVR: boolean;
  duration: number;
  stagesViewed: number;
  avgFps: number;
}

export interface StageViewAnalytics {
  id: string;
  sessionId: string;
  sequenceId: string | null;
  stageIndex: number;
  stageName: string;
  timestamp: Timestamp;
  duration: number;
  avgFps: number;
}
